import { create } from 'zustand';
import { mockApi } from '@/lib/mockApi';
import { api } from '@/lib/api';
import type { User, UserRole } from '@/types';

interface AuthState {
  user: User | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  setRole: (role: UserRole) => void;
  checkAuth: () => Promise<void>;
}

const isHttpMode = () => {
  if (typeof window === 'undefined') return false;
  return process.env.NEXT_PUBLIC_DATA_SOURCE === 'http';
};

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  isLoading: true,
  
  checkAuth: async () => {
    if (isHttpMode()) {
      try {
        const { user } = await api.auth.me();
        set({ user, isLoading: false });
      } catch (error) {
        // Not authenticated
        set({ user: null, isLoading: false });
      }
    } else {
      // Mock mode
      const user = mockApi.getCurrentUser();
      set({ user, isLoading: false });
    }
  },
  
  login: async (email: string, password: string) => {
    if (isHttpMode()) {
      const { user } = await api.auth.login({ email, password });
      set({ user });
    } else {
      // Mock mode: use password as OTP for compatibility
      const user = await mockApi.login(email, password || '123456');
      set({ user });
    }
  },
  
  register: async (email: string, password: string) => {
    if (isHttpMode()) {
      // Register now returns { ok: true } - user must verify email before login
      await api.auth.register({ email, password });
      // Don't set user - they need to verify email first
    } else {
      // Mock mode: simulate registration by logging in (auto-verified in mock)
      const user = await mockApi.login(email, '123456');
      set({ user });
    }
  },
  
  logout: async () => {
    if (isHttpMode()) {
      await api.auth.logout();
    } else {
      await mockApi.logout();
    }
    set({ user: null });
  },
  
  setRole: (role: UserRole) => {
    if (!isHttpMode()) {
      mockApi.setRole(role);
      const user = mockApi.getCurrentUser();
      if (user) {
        set({ user });
      }
    }
    // In HTTP mode, role changes require backend update
  },
}));

